import { WebhookGatewayMessage } from '../../../../../types/WebhookMessages';
import { StatePlaceholder } from '../../../../cdl/StatePlaceholder/StatePlaceholder';
import { TableBuilder } from '../../../../cdl/TableBuilder/TableBuilder';
import { TableBuilderColumn } from '../../../../cdl/TableBuilder/TableBuilderColumn';
import { Tag } from '../../../../cdl/Tag/Tag';
import { Text } from '../../../../cdl/Text/Text';
import { GenericError } from '../../../../common/Error/GenericError';
import { formatCompanyName } from '../../../../common/helpers/formatCompanyName.helper';
import { translate } from '../../../../common/helpers/translate.helper';
import { truncateMiddle } from '../../../../common/helpers/truncateMiddle.helper';
import { useEnums } from '../../../../common/hooks/useEnums';
import { IconFishHook } from '../../../../common/icons/cdl/FishHook';
import { ListingWithMore } from '../../../../common/ListingWithMore/ListingWithMore';
import { LoadingIndicator } from '../../../../common/LoadingIndicator/LoadingIndicator';
import { Box } from '../../../../common/ui/Box';
import { HoverableCopyToClipboardButton } from '../../../components/HoverableCopyToClipboardButton';
import { useWebhookList } from '../hooks/useWebhookList';

import { WebhookTableDropdown } from './WebhookTableDropdown';

interface WebhookTableProps {
    groupId: string;
}

export const WebhookTable = ({ groupId }: WebhookTableProps) => {
    const webhookListQuery = useWebhookList(groupId);

    const { getHumanReadableValue } = useEnums();

    if (webhookListQuery.isPending) {
        return <LoadingIndicator />;
    }

    if (webhookListQuery.isError) {
        return <GenericError />;
    }

    if (!webhookListQuery.data.length) {
        return (
            <Box marginTop="124px" display="flex" justifyContent="center">
                <StatePlaceholder
                    title={translate('webhooks.emptyState.title')}
                    description={translate('webhooks.emptyState.description')}
                    Icon={IconFishHook}
                />
            </Box>
        );
    }

    return (
        <TableBuilder data={webhookListQuery.data}>
            <TableBuilderColumn<WebhookGatewayMessage> header={translate('webhooks.destination')}>
                {(row) => {
                    return (
                        <Box display="grid">
                            <Text variant="small" fontWeight={600}>
                                {row.name}
                            </Text>
                            <HoverableCopyToClipboardButton copyValue={row.url} size="small" color="foreground.subtle">
                                <Text
                                    variant="extraSmall"
                                    style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                >
                                    {row.url}
                                </Text>
                            </HoverableCopyToClipboardButton>
                        </Box>
                    );
                }}
            </TableBuilderColumn>
            <TableBuilderColumn<WebhookGatewayMessage> header={translate('webhooks.events')}>
                {(row) => {
                    return (
                        <ListingWithMore>
                            {row.resourceTypes.map((type) => getHumanReadableValue('webhookResourceType', type))}
                        </ListingWithMore>
                    );
                }}
            </TableBuilderColumn>
            <TableBuilderColumn<WebhookGatewayMessage> header={translate('apikey.companies')}>
                {(row) => {
                    const customerNames = row.customers.map(
                        (customer) => formatCompanyName({ company: customer }) as string
                    );

                    return <ListingWithMore>{customerNames}</ListingWithMore>;
                }}
            </TableBuilderColumn>
            <TableBuilderColumn<WebhookGatewayMessage> header={translate('webhooks.signingSecret')} width="150px">
                {(row) => {
                    return (
                        <HoverableCopyToClipboardButton copyValue={row.secret}>
                            <Text variant="small">{truncateMiddle(row.secret)}</Text>
                        </HoverableCopyToClipboardButton>
                    );
                }}
            </TableBuilderColumn>
            <TableBuilderColumn<WebhookGatewayMessage> header={translate('webhooks.status')} width="150px">
                {(row) => {
                    if (!row.enabled) {
                        return <Tag variant="default">{translate('webhooks.status.disabled')}</Tag>;
                    }
                    return <Tag variant="positive">{translate('webhooks.status.active')}</Tag>;
                }}
            </TableBuilderColumn>
            <TableBuilderColumn<WebhookGatewayMessage> header="" width="64px">
                {(row) => {
                    return <WebhookTableDropdown webhook={row} groupId={groupId} />;
                }}
            </TableBuilderColumn>
        </TableBuilder>
    );
};
